// Coin economy — client mirror. The server is authoritative for both payouts
// (functions/src/callable/shared.ts) and prices/ownership
// (functions/src/callable/shop.ts + rooms.ts); this module only renders the
// storefront, reads the caller's own wallet doc, and remembers what's equipped.

import type { CSSProperties } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { cardBackArt, cardBackVariant } from './art';
import { setEquipped as setEquippedRemote } from './api';
import { NAME_COLORS, hasSavedNameColor, setSavedNameColor, type NameColorId } from './cosmetics';

/** Payout table shown in the shop's "how to earn" hint. */
export const COIN_RULES = { perGame: 10, winBonus: 20, mvpBonus: 15 } as const;

// Daily login bonus (mirror of functions/src/callable/shop.ts): a 7-step
// ladder that tops out at ⬡100, a personal 24h cooldown from the moment of
// the previous claim, and a 48h grace window before the ladder resets.
export const DAILY_LADDER = [10, 20, 30, 45, 60, 80, 100] as const;
export const DAILY_COOLDOWN_MS = 24 * 60 * 60 * 1000;
export const DAILY_GRACE_MS = 48 * 60 * 60 * 1000;

export function dailyReward(streak: number): number {
  return DAILY_LADDER[Math.min(Math.max(streak, 1), DAILY_LADDER.length) - 1]!;
}

export type ShopItemKind = 'color' | 'frame' | 'cardback';

export interface ShopItem {
  /** Namespaced id matching the server catalog, e.g. "frame.gold". */
  id: string;
  kind: ShopItemKind;
  /** Bare id within its kind, e.g. "gold" — what gets equipped/persisted. */
  key: string;
  price: number;
}

// Avatar frames: neon rings drawn around the avatar of everyone who can see
// you in a lobby/game (the server writes the id into the public player doc).
export const SHOP_FRAMES: (ShopItem & { color: string })[] = [
  { id: 'frame.cyan', kind: 'frame', key: 'cyan', price: 400, color: '#00e5ff' },
  { id: 'frame.magenta', kind: 'frame', key: 'magenta', price: 400, color: '#ff2e88' },
  { id: 'frame.gold', kind: 'frame', key: 'gold', price: 800, color: '#ffd700' },
];

// Card backs: purely local rendering preference (how YOU see face-down cards);
// ownership is still validated server-side at purchase time. Each is a full
// generated art skin (tools/generate_neonfall_art.py → card-back-<key>.webp).
export const SHOP_CARDBACKS: (ShopItem & { art: string })[] = [
  { id: 'cardback.cyan', kind: 'cardback', key: 'cyan', price: 500, art: cardBackVariant('cyan') },
  { id: 'cardback.magenta', kind: 'cardback', key: 'magenta', price: 500, art: cardBackVariant('magenta') },
  { id: 'cardback.violet', kind: 'cardback', key: 'violet', price: 600, art: cardBackVariant('violet') },
  { id: 'cardback.gold', kind: 'cardback', key: 'gold', price: 1000, art: cardBackVariant('gold') },
];

const CARDBACK_BY_KEY = new Map(SHOP_CARDBACKS.map((c) => [c.key, c]));
const FRAME_KEYS = new Set(SHOP_FRAMES.map((f) => f.key));

/** Inline style for a face-down card: the equipped skin, else the stock art. */
export function cardBackCss(key?: string | null): CSSProperties {
  const item = key ? CARDBACK_BY_KEY.get(key) : undefined;
  return {
    backgroundImage: `url(${item ? item.art : cardBackArt})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  };
}

// ---------------------------------------------------------------------------
// Wallet (wallets/{uid} — owner-readable, Cloud-Functions-written)
// ---------------------------------------------------------------------------

export interface Wallet {
  coins: number;
  /** Lifetime coins earned (spending doesn't reduce this). */
  earned: number;
  owned: string[];
  /** Payout of the most recently finished game, keyed by its seed. */
  lastGame: { gameId: string; coins: number } | null;
  /** Epoch ms of the last collected daily bonus (0 = never claimed). */
  dailyAt: number;
  dailyStreak: number;
  /** Equipped cosmetics — account-wide, so they follow the player across browsers/devices. */
  equippedFrame: string | null;
  equippedCardBack: string | null;
  equippedNameColor: string | null;
}

export const EMPTY_WALLET: Wallet = {
  coins: 0,
  earned: 0,
  owned: [],
  lastGame: null,
  dailyAt: 0,
  dailyStreak: 0,
  equippedFrame: null,
  equippedCardBack: null,
  equippedNameColor: null,
};

export async function fetchWallet(uid: string): Promise<Wallet> {
  const snap = await getDoc(doc(db, 'wallets', uid));
  const x = snap.exists() ? snap.data() : {};
  const n = (v: unknown) => (typeof v === 'number' ? v : 0);
  const str = (v: unknown) => (typeof v === 'string' ? v : null);
  const lg = x.lastGame as { gameId?: unknown; coins?: unknown } | undefined;
  const wallet: Wallet = {
    coins: n(x.coins),
    earned: n(x.earned),
    owned: Array.isArray(x.owned) ? x.owned.filter((i): i is string => typeof i === 'string') : [],
    lastGame:
      lg && typeof lg.gameId === 'string' && typeof lg.coins === 'number'
        ? { gameId: lg.gameId, coins: lg.coins }
        : null,
    dailyAt: n(x.dailyAt),
    dailyStreak: n(x.dailyStreak),
    equippedFrame: str(x.equippedFrame),
    equippedCardBack: str(x.equippedCardBack),
    equippedNameColor: str(x.equippedNameColor),
  };
  syncEquippedFromWallet(wallet);
  return wallet;
}

// ---------------------------------------------------------------------------
// Reconciling the local (per-browser) equip cache with the wallet (per-account)
// ---------------------------------------------------------------------------

const VALID_NAME_COLORS = new Set(NAME_COLORS.map((c) => c.id));

/**
 * Make this browser's cached equip choice match the account's. The wallet is
 * the source of truth once it has an opinion; the one-time exception is a
 * browser that picked something *before* this synced server-side — in that
 * case push the local choice up so other browsers/devices pick it up too.
 */
function syncEquippedFromWallet(w: Wallet): void {
  if (w.equippedFrame !== null) {
    setSavedFrame(w.equippedFrame);
  } else if (savedFrame()) {
    setEquippedRemote('frame', savedFrame()).catch(() => {});
  }

  if (w.equippedCardBack !== null) {
    setSavedCardBack(w.equippedCardBack);
  } else if (savedCardBack()) {
    setEquippedRemote('cardback', savedCardBack()).catch(() => {});
  }

  if (w.equippedNameColor !== null && VALID_NAME_COLORS.has(w.equippedNameColor as NameColorId)) {
    setSavedNameColor(w.equippedNameColor as NameColorId);
  } else {
    const local = hasSavedNameColor();
    if (local) setEquippedRemote('nameColor', local).catch(() => {});
  }
}

// ---------------------------------------------------------------------------
// Equipped cosmetics (local persistence, same pattern as savedNameColor)
// ---------------------------------------------------------------------------

const FRAME_KEY = 'neonfall.frame';
const CARDBACK_KEY = 'neonfall.cardBack';

/** Equipped avatar-frame key, or null for no frame. */
export function savedFrame(): string | null {
  const v = localStorage.getItem(FRAME_KEY);
  return v && FRAME_KEYS.has(v) ? v : null;
}

export function setSavedFrame(key: string | null): void {
  try {
    if (key) localStorage.setItem(FRAME_KEY, key);
    else localStorage.removeItem(FRAME_KEY);
  } catch {
    /* private mode — the frame just won't persist */
  }
}

/** Equipped card-back key, or null for the stock art. */
export function savedCardBack(): string | null {
  const v = localStorage.getItem(CARDBACK_KEY);
  return v && CARDBACK_BY_KEY.has(v) ? v : null;
}

export function setSavedCardBack(key: string | null): void {
  try {
    if (key) localStorage.setItem(CARDBACK_KEY, key);
    else localStorage.removeItem(CARDBACK_KEY);
  } catch {
    /* private mode — the skin just won't persist */
  }
}

// ---------------------------------------------------------------------------
// Equipping — updates this browser's cache immediately and persists to the
// wallet doc so the choice follows the account across browsers/devices.
// ---------------------------------------------------------------------------

export function equipFrame(key: string | null): Promise<unknown> {
  setSavedFrame(key);
  return setEquippedRemote('frame', key);
}

export function equipCardBack(key: string | null): Promise<unknown> {
  setSavedCardBack(key);
  return setEquippedRemote('cardback', key);
}

export function equipNameColor(id: NameColorId): Promise<unknown> {
  setSavedNameColor(id);
  return setEquippedRemote('nameColor', id);
}
